import React, { useState, useEffect } from "react";
import {
    Table,
    Card,
    Placeholder,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useActivity } from "@features/admin/activity/hooks/useActivity";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import { FiEdit2, FiSlash, FiCheckCircle } from "react-icons/fi";
import { Activity } from "@features/admin/activity/types/activity";
import UpdateActivityModal from "./UpdateActivityModal";
import AssignActivityKPIModal from "./assignActivityKPIModal";
import StatusChangeConfirmModal, { StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import { useActivityAction } from "@features/admin/activity/hooks/useActivityAction";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import NoResult from "@core/components/NoResult";
import { useDebounce } from "@core/hooks/useDebounce";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { hasActiveListCriteria } from "@core/utils/listQuery";
import { useScopeId } from "@core/hooks/useRouteParamId";
import {
    NAME_SORT_FILTER_FIELD,
    SORT_BY_ACTIVITY_NAME,
    SORT_DIR_ASC,
    countContentFilters,
    contentFilterKeys,
    filtersWithoutSort,
    normalizeSortDir,
    readSortDirFromSearchParams,
    withCatalogSort,
} from "@core/constants/listSort";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";

const ACTIVITY_STATUS_ACTION_CODES = [ACTION_CODES.ENABLE_ACTIVITY, ACTION_CODES.DISABLE_ACTIVITY];

const ACTIVITY_FILTER_FIELDS: FilterField[] = [
    NAME_SORT_FILTER_FIELD,
    { name: 'activityName',       label: 'Activity Name',    type: 'text', placeholder: 'Filter by activity name' },
    { name: 'kpiName',            label: 'KPI',              type: 'text', placeholder: 'Filter by KPI name' },
    { name: 'reportTemplateName', label: 'Report Template',  type: 'text', placeholder: 'Filter by template name' },
    { name: 'description',        label: 'Description',      type: 'text', placeholder: 'Filter by description' },
    { name: 'isActive',           label: 'Status',           type: 'select', options: [{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }] },
];

const ActivityTable: React.FC<{ showPagination?: boolean; refreshSignal?: number }> = ({ showPagination = false, refreshSignal }) => {
    const [searchParams] = useSearchParams();
    const resolvedNationId = useScopeId() ?? 0;

    const [page, setPage] = useState(() => Number(searchParams.get("page") || "1"));
    const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
    const debouncedSearch = useDebounce(search, 300);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const result: Record<string, any> = {};
        searchParams.forEach((value, key) => {
            if (key !== "q" && key !== "page") result[key] = value;
        });
        result.sortDir = readSortDirFromSearchParams(searchParams);
        return result;
    });
    const hasSearchOrFilters =
        !!debouncedSearch ||
        hasActiveListCriteria(activeFilters, contentFilterKeys(Object.keys(activeFilters)));

    useSearchUrlSync({ query: debouncedSearch, filters: activeFilters, page });

    // Selected activity for modals
    const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
    const [showEditActivityModal, setShowEditActivityModal] = useState(false);
    const [showAssignActivityKPIModal, setShowAssignActivityKPIModal] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState<{ activity: Activity; kind: StatusChangeKind } | null>(null);

    const showError = useApiError();
    const { busy: statusBusy, doActivityDisable, doActivityEnable } = useActivityAction();

    // ADMIN-only enable/disable gated on NATION-scoped action codes.
    const { hasPermission } = usePermissions({
        scopeType: 'NATION',
        scopeId: resolvedNationId || undefined,
        actionCodes: ACTIVITY_STATUS_ACTION_CODES,
    });
    const canDisable = hasPermission(ACTION_CODES.DISABLE_ACTIVITY);
    const canEnable = hasPermission(ACTION_CODES.ENABLE_ACTIVITY);

    // Fetch activities using custom hook
    const { current_page, data, from, last_page, per_page, total, loading, reload } = useActivity(
        withCatalogSort(
            {
                nationId: resolvedNationId,
                page,
                ...(hasSearchOrFilters ? {} : { all: true }),
                ...(debouncedSearch ? { freetext: debouncedSearch } : {}),
                ...filtersWithoutSort(activeFilters),
                sortDir: normalizeSortDir(activeFilters.sortDir),
            },
            SORT_BY_ACTIVITY_NAME
        )
    );

    // Calculate total pages
    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({
        from,
        page: pageNumber,
        perPage: per_page,
        total,
        currentCount: data.length,
    });


    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    // Reload when a sibling create/edit succeeds (skip the initial mount).
    const didMountRef = React.useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    const handleStatusConfirm = async () => {
        if (!statusConfirm || !resolvedNationId) return;
        const { activity, kind } = statusConfirm;
        const response = kind === "disable"
            ? await doActivityDisable(resolvedNationId, Number(activity.activity_id))
            : await doActivityEnable(resolvedNationId, Number(activity.activity_id));

        if (response.isSuccess()) {
            showSuccess(response.getData()?.message ?? `Activity ${kind}d successfully.`);
            setStatusConfirm(null);
            void reload();
        } else {
            showError(response.getError() as ErrorResponse);
        }
    };

    // Shared by the desktop table row and the mobile card so the action set
    // (and its permission gating) stays defined in one place.
    const renderActivityActions = (r: (typeof data)[number]) => {
        const id = Number(r.activity_id);
        return (
            <TableActions aria-label={`Actions for Activity ${r.activity_name}`}>
                <TableActionButton
                    icon={<FiEdit2 aria-hidden="true" />}
                    label="Edit"
                    primary
                    onClick={() => { setSelectedActivity(r); setShowEditActivityModal(true); }}
                    data-testid={`activity-edit-${id}`}
                />
                <TableActionButton
                    icon="journal-plus"
                    label="Assign KPI"
                    primary
                    onClick={() => { setSelectedActivity(r); setShowAssignActivityKPIModal(true); }}
                    data-testid={`activity-assign-kpi-${id}`}
                />
                {r.is_active && canDisable && (
                    <TableActionButton
                        icon={<FiSlash />}
                        label="Disable"
                        variant="outline-secondary"
                        className="ntcog-table-action-btn--danger"
                        onClick={() => setStatusConfirm({ activity: r, kind: "disable" })}
                        data-testid={`activity-disable-${id}`}
                    />
                )}
                {!r.is_active && canEnable && (
                    <TableActionButton
                        icon={<FiCheckCircle />}
                        label="Enable"
                        variant="outline-secondary"
                        className="ntcog-table-action-btn--success"
                        onClick={() => setStatusConfirm({ activity: r, kind: "enable" })}
                        data-testid={`activity-enable-${id}`}
                    />
                )}
            </TableActions>
        );
    };

    return (
        <div data-testid="activity-table">
            <div className="ntcog-list-search-section">
                                <SearchBar
                    placeholder="Search activities…"
                    filterFields={ACTIVITY_FILTER_FIELDS}
                    initialQuery={search}
                    initialFilters={activeFilters}
                    onQueryChange={(q) => { setSearch(q); setPage(1); }}
                    onFiltersApply={(f) => {
                        setActiveFilters({ ...f, sortDir: normalizeSortDir(f.sortDir) });
                        setPage(1);
                    }}
                    onClear={() => {
                        setSearch("");
                        setActiveFilters({ sortDir: SORT_DIR_ASC });
                        setPage(1);
                    }}
                    loading={loading}
                    activeFilterCount={countContentFilters(activeFilters)}
                    testId="activity-search"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="activity-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive
                            hover
                            size="sm"
                            className="mb-0 align-middle table-striped ntcog-table" data-testid="activity-table"
                        >
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th>#</th>
                                    <th>Activity Name</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 140 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-group-divider" style={{ opacity: loading && data.length > 0 ? 0.5 : 1, transition: "opacity 0.2s ease" }}>
                                {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`} data-testid={`skeleton-row-${i}`}>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={5} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr data-testid="no-activities-row">
                                        <td colSpan={5} className="py-5">
                                            <NoResult line_1="No Activities found." line_2="Try adjusting filters or create a new Activity." />
                                        </td>

                                    </tr>
                                )}
                                {data.length > 0 && data.map((r: typeof data[number], i: number) => {
                                    const id = Number(r.activity_id);
                                    const rowNumber = (from != null) ? from + i : i + 1;
                                    return (
                                        <tr key={id} data-testid={`activity-row-${id}`} className="bg-white">
                                            <td>
                                                {rowNumber}
                                            </td>
                                            <td className="fw-semibold">{r.activity_name}</td>
                                            <td >{r.description}</td>
                                            <td><StatusPill active={!!r.is_active} /></td>
                                            <td className="text-end">
                                                {renderActivityActions(r)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="activity-cards">
                        {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`activity-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult line_1="No Activities found." line_2="Try adjusting filters or create a new Activity." />
                        )}
                        {data.length > 0 && data.map((r: typeof data[number]) => {
                            const id = Number(r.activity_id);
                            return (
                                <div key={id} className="ntcog-card-list__item">
                                    <EntityCard
                                        testId={`activity-card-${id}`}
                                        ariaLabel={`Activity ${r.activity_name}`}
                                        title={<span className="fw-semibold">{r.activity_name}</span>}
                                        status={<StatusPill active={!!r.is_active} />}
                                        actions={renderActivityActions(r)}
                                        description={r.description || undefined}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="pagination-bar"
                    info={`Showing ${start} to ${end} of ${total} activities`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}

            <UpdateActivityModal
                show={showEditActivityModal}
                onHide={() => setShowEditActivityModal(false)}
                activity={selectedActivity}
                onChanged={reload}
            />

            <AssignActivityKPIModal
                show={showAssignActivityKPIModal}
                onHide={() => setShowAssignActivityKPIModal(false)}
                activity={selectedActivity}
            />

            <StatusChangeConfirmModal
                show={!!statusConfirm}
                kind={statusConfirm?.kind ?? "disable"}
                entityLabel="activity"
                entityName={statusConfirm?.activity.activity_name ?? ""}
                busy={statusBusy}
                onConfirm={handleStatusConfirm}
                onCancel={() => setStatusConfirm(null)}
                bodyText={statusConfirm?.kind === "disable" ? (
                    <>
                        Are you sure you want to disable <strong>{statusConfirm?.activity.activity_name}</strong>? It can no longer be assigned to report templates. Existing report data is unaffected.
                    </>
                ) : undefined}
            />
        </div>
    );
};

export default ActivityTable;